import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { formatCad, type AppointmentView, type Technician } from "@shared/modules/x-atelier";
import { internalBlocksForDay, type AgendaRules, type InternalBlock } from "@shared/modules/x-site";
import { ApiError } from "../../../core/lib/api";
import { moduleEnabled } from "../../../core/blueprint";
import { Modal } from "../../../core/components/ui/modal";
import { Button } from "../../../core/components/ui/button";
import { Icon } from "../../../core/components/ui/icon";
import { PageHeader } from "../../../core/components/ui/page-header";
import { toDate } from "../../../core/lib/format";
import { AppointmentCard } from "../components/appointment-card";
import { AppointmentModal, type AppointmentPreset } from "../components/appointment-modal";
import { DayStrip } from "../components/day-strip";
import { TechChips } from "../components/tech-chips";
import { useAuthStore } from "../../../core/stores/auth";
import { useToastStore } from "../../../core/stores/toast";
import { ClosuresManager } from "../components/closures-manager";
import {
  getAgendaRules,
  updateAppointment,
  getStats,
  listAppointments,
  listTechnicians,
  type AtelierStats,
} from "../lib/api";
import {
  addDays,
  addMonths,
  dayKey,
  formatDayLong,
  formatHours,
  formatMonth,
  formatWeekRange,
  formatWeekday,
  isToday,
  monthGrid,
  parseDayKey,
  startOfDay,
  startOfMonth,
  startOfWeek,
  weekDays,
} from "../lib/dates";
import { isInactive } from "../lib/status";
import { CalendarEvents } from "../components/calendar-events";
import { ColorLegend } from "../components/color-legend";
import { TimeGrid } from "../components/time-grid";

// « agenda » = bande des sept jours + liste du jour choisi : la vue de la
// tablette. « semaine » = sept colonnes, pour un grand écran au comptoir.
type View = "agenda" | "semaine" | "mois";
const VIEWS: Array<{ key: View; label: string }> = [
  { key: "agenda", label: "Jour" },
  { key: "semaine", label: "Semaine" },
  { key: "mois", label: "Mois" },
];

// « horaire » = la grille des heures, comme sur un mur d'atelier ; « liste » =
// les cartes empilées, plus lisibles sur un téléphone. Le choix suit l'URL.
type Mode = "horaire" | "liste";

/** Vue par défaut : la journée sur un écran tactile ou étroit, la semaine sinon. */
function defaultView(): View {
  if (typeof window === "undefined") return "semaine";
  return window.matchMedia("(max-width: 1279px), (pointer: coarse)").matches ? "agenda" : "semaine";
}

/** Bornes [from, to[ de la période affichée (la journée charge sa semaine pour la bande). */
function rangeOf(view: View, anchor: Date): { from: Date; to: Date } {
  if (view === "mois") {
    const days = monthGrid(anchor);
    return { from: days[0]!, to: addDays(days[days.length - 1]!, 1) };
  }
  const from = startOfWeek(anchor);
  return { from, to: addDays(from, 7) };
}

function shift(view: View, anchor: Date, dir: 1 | -1): Date {
  if (view === "agenda") return addDays(anchor, dir);
  if (view === "mois") return addMonths(anchor, dir);
  return addDays(anchor, 7 * dir);
}

/** Agenda d'atelier : jour (bande + liste), semaine (7 colonnes) ou mois (compte et total par jour). */
export function AgendaPage() {
  const push = useToastStore((s) => s.push);
  const [searchParams, setSearchParams] = useSearchParams();
  const rawView = searchParams.get("vue");
  const view: View =
    rawView === "jour"
      ? "agenda"
      : VIEWS.some((v) => v.key === rawView)
        ? (rawView as View)
        : defaultView();
  const anchor = useMemo(
    () => parseDayKey(searchParams.get("date")) ?? startOfDay(new Date()),
    [searchParams],
  );
  const technicianId = searchParams.get("technicien") ?? "";
  const mode: Mode = searchParams.get("mode") === "liste" ? "liste" : "horaire";

  const [technicians, setTechnicians] = useState<Technician[]>([]);
  const [appointments, setAppointments] = useState<AppointmentView[]>([]);
  const [stats, setStats] = useState<AtelierStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [modal, setModal] = useState<{ open: boolean; preset?: AppointmentPreset }>({
    open: false,
  });

  const setParams = (changes: Record<string, string | null>) => {
    const next = new URLSearchParams(searchParams);
    for (const [k, v] of Object.entries(changes)) {
      if (v === null || v === "") next.delete(k);
      else next.set(k, v);
    }
    setSearchParams(next, { replace: true });
  };

  const range = useMemo(() => rangeOf(view, anchor), [view, anchor]);

  useEffect(() => {
    listTechnicians()
      .then(setTechnicians)
      .catch(() => setTechnicians([]));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    const from = range.from.toISOString();
    const to = range.to.toISOString();
    try {
      const [items, s] = await Promise.all([
        listAppointments({ from, to, technicianId: technicianId || undefined }),
        getStats(from, to),
      ]);
      setAppointments(items);
      setStats(s);
      setFailed(false);
    } catch {
      setAppointments([]);
      setStats(null);
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [range, technicianId]);

  useEffect(() => {
    void load();
  }, [load]);

  // Pause de midi et fermetures, dessinées dans l'horaire (mêmes règles que le site et Léo).
  const [rules, setRules] = useState<AgendaRules | null>(null);
  const [closuresOpen, setClosuresOpen] = useState(false);
  const isAdmin = useAuthStore((s) => s.user?.role === "admin");
  useEffect(() => {
    if (!moduleEnabled("x-site")) return;
    getAgendaRules()
      .then(setRules)
      .catch(() => setRules(null));
  }, []);
  const blocksFor = useCallback(
    (d: Date) => (rules ? internalBlocksForDay(dayKey(d), rules, rules.timeZone) : []),
    [rules],
  );

  // Déplacement sur la pause ou une fermeture : dérogation à confirmer.
  const [override, setOverride] = useState<{
    appointment: AppointmentView;
    start: Date;
    end: Date;
    message: string;
  } | null>(null);

  const reschedule = async (
    appointment: AppointmentView,
    start: Date,
    end: Date,
    allowInternalBlock = false,
  ) => {
    try {
      const saved = await updateAppointment(appointment.id, {
        startAt: start.toISOString(),
        endAt: end.toISOString(),
        allowInternalBlock: allowInternalBlock || undefined,
      });
      setAppointments((items) => items.map((item) => (item.id === saved.id ? saved : item)));
      setStats(null);
      // Le client d'un rendez-vous à venir reçoit le nouvel horaire (texto, deux minutes après).
      const told =
        (saved.status === "reserve" || saved.status === "confirme") &&
        Boolean(saved.clientPhone) &&
        start.getTime() > Date.now() &&
        toMsSafe(saved.startAt) !== toMsSafe(appointment.startAt);
      push(
        "success",
        "Horaire mis à jour",
        told ? `${saved.title} · Le client recevra le nouvel horaire par texto.` : saved.title,
      );
    } catch (err) {
      if (err instanceof ApiError && err.code === "internal_block" && !allowInternalBlock) {
        const details = err.details as { message?: string } | undefined;
        setOverride({
          appointment,
          start,
          end,
          message: details?.message ?? "Ce déplacement chevauche une indisponibilité interne.",
        });
        return;
      }
      push("danger", "Modification impossible", "L'horaire précédent a été conservé. Réessayez.");
    }
  };

  // Rendez-vous regroupés par jour (clé locale), déjà triés par début par l'API.
  const byDay = useMemo(() => {
    const map = new Map<string, AppointmentView[]>();
    for (const a of appointments) {
      const d = toDate(a.startAt);
      if (!d) continue;
      const key = dayKey(d);
      const list = map.get(key);
      if (list) list.push(a);
      else map.set(key, [a]);
    }
    return map;
  }, [appointments]);

  const counts = useMemo(() => {
    const map = new Map<string, number>();
    for (const [key, list] of byDay) map.set(key, list.filter((a) => !isInactive(a.status)).length);
    return map;
  }, [byDay]);

  // Bandeau : en vue Jour, les chiffres décrivent la journée affichée ; sinon
  // la période. Filtré par technicien, on recompte localement.
  const totals = useMemo(() => {
    if (view === "agenda" || technicianId || !stats) {
      const source = view === "agenda" ? (byDay.get(dayKey(anchor)) ?? []) : appointments;
      const active = source.filter((a) => !isInactive(a.status));
      return {
        count: active.length,
        durationMin: active.reduce(
          (sum, a) => sum + Math.max(0, (toMsSafe(a.endAt) - toMsSafe(a.startAt)) / 60_000),
          0,
        ),
        totalCents: active.reduce((sum, a) => sum + a.totalCents, 0),
      };
    }
    return {
      count: stats.appointmentCount,
      durationMin: stats.durationMin,
      totalCents: stats.totalCents,
    };
  }, [appointments, byDay, anchor, stats, technicianId, view]);

  const periodLabel =
    view === "agenda"
      ? formatDayLong(anchor)
      : view === "mois"
        ? formatMonth(anchor)
        : formatWeekRange(anchor);

  const openDay = (d: Date) => setParams({ vue: "agenda", date: dayKey(d) });
  // Clic sur une case libre : le rendez-vous s'ouvre déjà à cette heure-là.
  const openSlot = (start: Date) => setModal({ open: true, preset: { startAt: start } });

  return (
    <div className="page agenda">
      <PageHeader
        title="Agenda"
        subtitle="Les rendez-vous de l'atelier, par technicien."
        actions={
          <>
            {isAdmin && moduleEnabled("x-site") ? (
              <Button
                variant="ghost"
                icon={<Icon name="clock" size={18} />}
                onClick={() => setClosuresOpen(true)}
              >
                Fermetures
              </Button>
            ) : null}
            <Button
              variant="primary"
              icon={<Icon name="plus" size={18} />}
              onClick={() =>
                setModal({ open: true, preset: { startAt: view !== "mois" ? anchor : undefined } })
              }
            >
              Nouveau rendez-vous
            </Button>
          </>
        }
      />

      <ClosuresManager
        open={closuresOpen}
        rules={rules}
        onClose={() => setClosuresOpen(false)}
        onSaved={setRules}
      />

      <CalendarEvents from={range.from} to={range.to} />
      <div className="agenda-bar">
        <div className="agenda-bar__nav" role="group" aria-label="Période">
          <Button
            variant="ghost"
            className="btn--icon"
            aria-label="Période précédente"
            icon={<Icon name="chevron-left" size={20} />}
            onClick={() => setParams({ date: dayKey(shift(view, anchor, -1)) })}
          />
          <Button
            variant={isToday(anchor) ? "primary" : "ghost"}
            onClick={() => setParams({ date: null })}
          >
            Aujourd'hui
          </Button>
          <Button
            variant="ghost"
            className="btn--icon"
            aria-label="Période suivante"
            icon={<Icon name="chevron-right" size={20} />}
            onClick={() => setParams({ date: dayKey(shift(view, anchor, 1)) })}
          />
          <h2 className="agenda-bar__period">{periodLabel}</h2>
        </div>
        <div className="agenda-bar__views">
          <div className="segmented" role="group" aria-label="Vue">
            {VIEWS.map((v) => (
              <button
                key={v.key}
                type="button"
                className={`segmented__btn${view === v.key ? " is-active" : ""}`}
                aria-pressed={view === v.key}
                onClick={() => setParams({ vue: v.key })}
              >
                {v.label}
              </button>
            ))}
          </div>
          {view !== "mois" ? (
            <div className="segmented" role="group" aria-label="Présentation">
              <button
                type="button"
                className={`segmented__btn${mode === "horaire" ? " is-active" : ""}`}
                aria-pressed={mode === "horaire"}
                onClick={() => setParams({ mode: null })}
              >
                Horaire
              </button>
              <button
                type="button"
                className={`segmented__btn${mode === "liste" ? " is-active" : ""}`}
                aria-pressed={mode === "liste"}
                onClick={() => setParams({ mode: "liste" })}
              >
                Liste
              </button>
            </div>
          ) : null}
        </div>
      </div>

      <TechChips
        technicians={technicians}
        value={technicianId}
        onChange={(id) => setParams({ technicien: id })}
      />

      <div className="atelier-stats" aria-label="Totaux de la période">
        <div className="atelier-stat atelier-stat--accent">
          <span className="atelier-stat__label">Montant</span>
          <span className="atelier-stat__value">{formatCad(totals.totalCents)}</span>
        </div>
        <div className="atelier-stat">
          <span className="atelier-stat__label">Heures</span>
          <span className="atelier-stat__value">{formatHours(totals.durationMin)}</span>
        </div>
        <div className="atelier-stat">
          <span className="atelier-stat__label">Rendez-vous</span>
          <span className="atelier-stat__value">{totals.count}</span>
        </div>
      </div>

      {view !== "mois" && mode === "horaire" ? (
        <>
          <ColorLegend />
          <p className="time-grid-help">
            Glissez un rendez-vous pour le déplacer. Étirez son bord supérieur ou inférieur pour
            modifier ses heures (par 15 minutes). Échap pour annuler.
          </p>
        </>
      ) : null}

      {failed ? (
        <p className="empty">Agenda indisponible — réessayez dans un instant.</p>
      ) : view === "agenda" ? (
        <>
          <DayStrip selected={anchor} counts={counts} onSelect={openDay} />
          {mode === "horaire" ? (
            <TimeGrid
              key={`day-${dayKey(anchor)}-${technicianId}`}
              days={[anchor]}
              byDay={byDay}
              loading={loading}
              onReschedule={reschedule}
              onSlot={openSlot}
              blocksFor={blocksFor}
            />
          ) : (
            <DayView items={byDay.get(dayKey(anchor)) ?? []} loading={loading} />
          )}
        </>
      ) : view === "semaine" ? (
        mode === "horaire" ? (
          <TimeGrid
            key={`week-${dayKey(startOfWeek(anchor))}-${technicianId}`}
            days={weekDays(anchor)}
            byDay={byDay}
            loading={loading}
            onReschedule={reschedule}
            onSlot={openSlot}
            onDay={openDay}
            blocksFor={blocksFor}
          />
        ) : (
          <WeekView anchor={anchor} byDay={byDay} loading={loading} onDay={openDay} />
        )
      ) : (
        <MonthView
          anchor={anchor}
          byDay={byDay}
          loading={loading}
          onDay={openDay}
          blocksFor={blocksFor}
        />
      )}

      <AppointmentModal
        open={modal.open}
        preset={modal.preset}
        onClose={() => setModal({ open: false })}
        onSaved={() => {
          setModal({ open: false });
          void load();
        }}
      />

      <Modal
        open={override !== null}
        title="Planifier pendant une indisponibilité ?"
        onClose={() => setOverride(null)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setOverride(null)}>
              Garder l’horaire actuel
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                const pending = override;
                setOverride(null);
                if (pending) void reschedule(pending.appointment, pending.start, pending.end, true);
              }}
            >
              Déplacer quand même
            </Button>
          </>
        }
      >
        <p>{override?.message}</p>
        <p>La dérogation sera consignée à l’historique du rendez-vous.</p>
      </Modal>
    </div>
  );
}

function toMsSafe(v: number | string): number {
  return toDate(v)?.getTime() ?? 0;
}

function WeekView({
  anchor,
  byDay,
  loading,
  onDay,
}: {
  anchor: Date;
  byDay: Map<string, AppointmentView[]>;
  loading: boolean;
  onDay: (d: Date) => void;
}) {
  const days = weekDays(anchor);
  const empty = !loading && days.every((d) => (byDay.get(dayKey(d)) ?? []).length === 0);
  return (
    <div className="agenda-week">
      {days.map((d) => {
        const items = byDay.get(dayKey(d)) ?? [];
        return (
          <section
            key={dayKey(d)}
            className={`agenda-week__day${isToday(d) ? " agenda-week__day--today" : ""}`}
            aria-label={formatDayLong(d)}
          >
            <button
              type="button"
              className="agenda-week__head"
              onClick={() => onDay(d)}
              title="Ouvrir la journée"
            >
              <span className="agenda-week__weekday">{formatWeekday(d)}</span>
              <span className="agenda-week__date">{d.getDate()}</span>
              {items.length > 0 ? <span className="agenda-week__count">{items.length}</span> : null}
            </button>
            <div className="agenda-week__cards">
              {items.map((a) => (
                <AppointmentCard key={a.id} appointment={a} />
              ))}
              {loading && items.length === 0 ? (
                <span className="agenda-week__placeholder" aria-hidden="true" />
              ) : null}
            </div>
          </section>
        );
      })}
      {empty ? <p className="agenda-week__empty">Aucun rendez-vous cette semaine.</p> : null}
    </div>
  );
}

function DayView({ items, loading }: { items: AppointmentView[]; loading: boolean }) {
  if (loading && items.length === 0) return <p className="empty">Chargement…</p>;
  if (items.length === 0) return <p className="empty">Aucun rendez-vous ce jour-là.</p>;
  return (
    <div className="agenda-day">
      {items.map((a) => (
        <AppointmentCard key={a.id} appointment={a} layout="wide" />
      ))}
    </div>
  );
}

function MonthView({
  anchor,
  byDay,
  loading,
  onDay,
  blocksFor,
}: {
  anchor: Date;
  byDay: Map<string, AppointmentView[]>;
  loading: boolean;
  onDay: (d: Date) => void;
  blocksFor: (d: Date) => InternalBlock[];
}) {
  const days = monthGrid(anchor);
  const month = startOfMonth(anchor).getMonth();
  const headers = weekDays(anchor);
  // Journée fermée (férié, fermeture) : une bande sur toute la journée.
  const closedLabel = (d: Date): string | null =>
    blocksFor(d).find((b) => b.kind === "fermeture" && b.end - b.start >= 23 * 3600_000)?.label ??
    null;
  return (
    <div className={`agenda-month${loading ? " agenda-month--loading" : ""}`}>
      {headers.map((d) => (
        <span key={dayKey(d)} className="agenda-month__weekday">
          {formatWeekday(d)}
        </span>
      ))}
      {days.map((d) => {
        const items = (byDay.get(dayKey(d)) ?? []).filter((a) => !isInactive(a.status));
        const total = items.reduce((sum, a) => sum + a.totalCents, 0);
        const closed = closedLabel(d);
        const classes = [
          "agenda-month__day",
          d.getMonth() !== month && "agenda-month__day--outside",
          isToday(d) && "agenda-month__day--today",
          closed && "agenda-month__day--closed",
        ]
          .filter(Boolean)
          .join(" ");
        return (
          <button
            key={dayKey(d)}
            type="button"
            className={classes}
            onClick={() => onDay(d)}
            aria-label={`${formatDayLong(d)} : ${items.length} rendez-vous${closed ? ` · fermé (${closed})` : ""}`}
          >
            <span className="agenda-month__num">{d.getDate()}</span>
            {closed ? (
              <span className="agenda-month__closed" title={closed}>
                Fermé · {closed}
              </span>
            ) : null}
            {items.length > 0 ? (
              <>
                <span className="agenda-month__count">{items.length} RDV</span>
                <span className="agenda-month__total">{formatCad(total)}</span>
              </>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
